import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Card,
  CardBody,
  Badge,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import Tooltip from '@material-ui/core/Tooltip';
import moment from 'moment';
import MatTableToTable from '@/shared/tables/table/MatTableToTable';
import Hooks from '@/shared/hooks';
import {
  selectFilteredDeals,
  selectPreviouslyPublishedRow,
  selectLoading,
  selectViewType,
  updateDeal,
  updateDealsByProspectId,
} from '../../../../redux/reducers/brand/ConfirmedDealsReducer';
import { formatCurrency } from '../../../../shared/helpers/WVFormatter';
import ChannelTypeLink from './ChannelTypeLink';
import CreatorInList from './CreatorInList';

const getConfidenceBadgeStyle = (confidence) => {
  switch (confidence?.toLowerCase()) {
    case 'high':
      return { backgroundColor: '#c8e6c9', color: '#43a047' };
    case 'medium':
      return { backgroundColor: '#ffe0b2', color: '#ff9800' };
    case 'low':
      return { backgroundColor: '#ffcdd2', color: '#e53935' };
    case 'published':
      return { backgroundColor: '#70bbfd', color: '#1a5490' };
    default:
      return { backgroundColor: '#e0e0e0', color: '#333333' };
  }
};

const formatCompactNumber = (num, decimalPlaces = 0) => {
  if (num === null || num === undefined) return '—';
  if (num === 0) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toFixed(decimalPlaces);
};

// Custom sort function that always puts null/empty values at the end
const nullsLastSortType = (rowA, rowB, columnId) => {
  const a = rowA.values[columnId];
  const b = rowB.values[columnId];

  // Check if values are null, undefined, empty string, or 0 (for numbers that represent "no value")
  const aIsEmpty = a === null || a === undefined || a === '' || a === '—';
  const bIsEmpty = b === null || b === undefined || b === '' || b === '—';

  // If both are empty, they're equal
  if (aIsEmpty && bIsEmpty) return 0;

  // If only a is empty, it should come after b (return 1)
  if (aIsEmpty) return 1;

  // If only b is empty, it should come after a (return -1)
  if (bIsEmpty) return -1;

  // Neither is empty, use default comparison
  if (a > b) return 1;
  if (a < b) return -1;
  return 0;
};

const DealsTable = ({
  deals,
  previouslyPublishedRow,
  loading,
  viewType,
  updateDeal: updateDealAction,
  updateDealsByProspectId: updateDealsByProspectIdAction,
}) => {
  const isPipeline = viewType === 'pipeline';
  // The "Previously Published Content" summary is rendered as a highlighted
  // full-width total below the table (not as a grid row).
  const tableData = deals;
  const { showSuccess } = Hooks.useNoticeHook();

  // Handle updates from child components (e.g., TrinityCheck)
  const handleDealUpdate = (dealId, updates, prospectId) => {
    if (prospectId) {
      // Update all deals with the same prospectId
      updateDealsByProspectIdAction({ prospectId, updates });
    } else {
      // Update single deal
      updateDealAction({ dealId, updates });
    }
  };

  const handleExportCSV = () => {
    const headers = isPipeline ? [
      'Creator Name',
      'Category',
      'Code',
      'Type',
      'Rate',
      'Release Date',
      'Confidence',
      'New/Rebuy',
      'Future Bookings',
      'Program Country',
    ] : [
      'Creator Name',
      'Category',
      'Code',
      'Type',
      'Rate',
      'Publish Date',
      'New/Rebuy',
      'Published Content',
      'Future Bookings',
      'Views',
      'V30',
      'ER30',
      'Conv.',
      'Clicks',
      'BATCAC',
      'BLVCAC',
      'IPCAC',
      'Comments',
      '% English',
      '% Female',
      '% Age 25+',
      'Program Country',
    ];

    const rows = deals.map(deal => {
      if (isPipeline) {
        return [
          deal.creator?.title || '',
          deal.category || '',
          deal.promoCode || '',
          deal.dealContentType || '',
          formatCurrency(deal.totalContractAmount || 0, deal.currency || 'usd', 2, true),
          deal.contentReleaseDate ? moment.utc(deal.contentReleaseDate).format('MM/DD/YY') : '',
          deal.confidence ? deal.confidence.charAt(0).toUpperCase() + deal.confidence.slice(1).toLowerCase() : '',
          deal.newOrExistingCreator ? (deal.newOrExistingCreator.toLowerCase() === 'new' ? 'New' : 'Rebuy') : '',
          deal.futureBooking?.filter(booking => booking.dealId !== deal.dealId).length || 0,
          deal.programCountry || '',
        ];
      }

      return [
        deal.creator?.title || '',
        deal.category || '',
        deal.promoCode || '',
        deal.dealContentType || '',
        formatCurrency(deal.totalContractAmount || 0, deal.currency || 'usd', 2, true),
        deal.publishDate ? moment.utc(deal.publishDate).format('MM/DD/YY') : '',
        deal.newOrExistingCreator ? (deal.newOrExistingCreator.toLowerCase() === 'new' ? 'New' : 'Rebuy') : '',
        deal.mediaContentTitle || '',
        deal.futureBooking?.filter(booking => booking.dealId !== deal.dealId).length || 0,
        deal.videoReportData?.videoViews ?? '—',
        deal.videoReportData?.v30 ?? '—',
        deal.videoReportData?.videoEr30 != null ? formatCompactNumber(deal.videoReportData.videoEr30, 2) : '—',
        deal.conversions || 0,
        deal.clicks || 0,
        deal.batCac != null ? formatCurrency(deal.batCac, deal.currency || 'usd', 2, true) : '',
        deal.blvCac != null ? formatCurrency(deal.blvCac, deal.currency || 'usd', 2, true) : '',
        deal.ipCac != null ? formatCurrency(deal.ipCac, deal.currency || 'usd', 2, true) : '',
        deal.videoReportData?.comments ?? '—',
        deal.videoReportData?.audienceEng != null ? `${deal.videoReportData.audienceEng.toFixed(1)}%` : '—',
        deal.videoReportData?.audienceFem != null ? `${deal.videoReportData.audienceFem.toFixed(0)}%` : '—',
        deal.videoReportData?.audienceAge25Plus != null ? `${deal.videoReportData.audienceAge25Plus.toFixed(0)}%` : '—',
        deal.programCountry || '',
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `confirmed-deals-${viewType}-${moment().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyCreatorCode = () => {
    const text = deals
      .map(deal => `${deal.creator?.title || ''}\t${deal.promoCode || ''}`)
      .join('\n');
    navigator.clipboard.writeText(text).then(() => {
      showSuccess('Creator names and promo codes copied to clipboard');
    });
  };

  // Define column groups for MatTableToTable
  // Groups: Basic Info | Deal Info (grey) | Performance Insights | Supporting Metrics (grey)
  const pipelineColumns = useMemo(() => ([
    {
      title: '',
      headerClassName: 'col-group-header',
      columns: {
        // --- Basic channel info ---
        creatorName: {
          title: 'Creator Name',
          Component: ({ record }) => <CreatorInList deal={record} onUpdate={handleDealUpdate} />,
          sort: record => record.creator?.title || '',
          sortType: nullsLastSortType,
          width: 250,
          minWidth: 250,
          isFreeze: true,
          left: 0,
          backgroundColor: '#fff',
        },
        category: {
          title: 'Category',
          Component: ({ record }) => {
            const style = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };
            if (record.category) {
              return (
                <Tooltip title={record.category}>
                  <span style={style}>{record.category}</span>
                </Tooltip>
              );
            }
            return (<span>—</span>);
          },
          sort: record => record.category || '',
          sortType: nullsLastSortType,
          width: 120,
        },
      },
    },
    {
      title: 'Deal Info',
      headerClassName: 'col-group-header col-group-header-shaded',
      columns: {
        // --- Deal Info (light grey) ---
        code: {
          title: 'Code',
          valueFunc: record => record.promoCode || '—',
          sort: record => record.promoCode || '',
          sortType: nullsLastSortType,
          width: 130,
          className: 'col-deal-info col-group-start',
        },
        channelType: {
          title: 'Type',
          Component: ({ record }) => (record.dealContentType ? <ChannelTypeLink deal={record} /> : <span>—</span>),
          sort: record => record.dealContentType || '',
          sortType: nullsLastSortType,
          width: 70,
          className: 'col-deal-info',
        },
        rate: {
          title: 'Rate',
          valueFunc: record => formatCurrency(record.totalContractAmount || 0, record.currency || 'usd', 2, true),
          sort: record => record.totalContractAmount || 0,
          sortType: nullsLastSortType,
          width: 90,
          className: 'col-deal-info',
        },
        contentReleaseDate: {
          title: 'Release Date',
          valueFunc: record => record.contentReleaseDate ? moment.utc(record.contentReleaseDate).format('MM/DD/YY') : '—',
          sort: record => record.contentReleaseDate || '',
          sortType: nullsLastSortType,
          width: 120,
          className: 'col-deal-info',
        },
        confidence: {
          title: 'Confidence',
          Component: ({ record }) => {
            if (!record.confidence) return '—';
            return (
              <Badge style={getConfidenceBadgeStyle(record.confidence)}>
                {record.confidence.charAt(0).toUpperCase() + record.confidence.slice(1).toLowerCase()}
              </Badge>
            );
          },
          sort: record => record.confidence || '',
          sortType: nullsLastSortType,
          width: 100,
          className: 'col-deal-info',
        },
        newOrRebuy: {
          title: 'New/Rebuy',
          Component: ({ record }) => {
            if (!record.newOrExistingCreator) return <span>—</span>;
            return (
              <Badge
                style={{
                  backgroundColor: record.newOrExistingCreator.toLowerCase() === 'new' ? '#70bbfd' : '#e0e0e0',
                  color: record.newOrExistingCreator.toLowerCase() === 'new' ? '#1a5490' : '#333333',
                }}
              >
                {record.newOrExistingCreator.toLowerCase() === 'new' ? 'New' : 'Rebuy'}
              </Badge>
            );
          },
          sort: record => record.newOrExistingCreator || '',
          sortType: nullsLastSortType,
          width: 100,
          className: 'col-deal-info',
        },
        futureBookings: {
          title: 'Future Bookings',
          Component: ({ record }) => {
            if (!record.futureBooking || record.futureBooking.length === 0) return <span>—</span>;

            // Filter out current deal from future bookings
            const otherDeals = record.futureBooking.filter(deal => deal.dealId !== record.dealId);

            if (otherDeals.length === 0) return <span>—</span>;

            // Build tooltip content - sort by date ascending
            const sortedDeals = [...otherDeals].sort((a, b) => {
              const dateA = a.contentReleaseDate ? new Date(a.contentReleaseDate) : new Date(0);
              const dateB = b.contentReleaseDate ? new Date(b.contentReleaseDate) : new Date(0);
              return dateA - dateB;
            });
            const totalAmount = otherDeals.reduce((sum, deal) => sum + (deal.amount || 0), 0);
            const tooltipContent = (
              <div style={{ fontSize: '12px' }}>
                {sortedDeals.map(deal => (
                  <div key={deal.dealId} style={{ marginBottom: '4px' }}>
                    <strong>
                      {moment.utc(deal.contentReleaseDate).format('MM/DD/YY')}
                    </strong> - {formatCurrency(deal.amount || 0, record.currency?.toLowerCase() || 'usd', 2, true)}
                  </div>
                ))}
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.3)' }}>
                  <strong>
                    Total Contract Amount: {formatCurrency(totalAmount, record.currency?.toLowerCase() || 'usd', 2, true)}
                  </strong>
                </div>
              </div>
            );

            return (
              <Tooltip title={tooltipContent} arrow>
                <a
                  href="#"
                  className="bold-text"
                >
                  +{otherDeals.length}
                </a>
              </Tooltip>
            );
          },
          sort: record => {
            const otherDeals = record.futureBooking?.filter(deal => deal.dealId !== record.dealId) || [];
            return otherDeals.length;
          },
          sortType: nullsLastSortType,
          width: 120,
          className: 'col-deal-info',
        },
      },
    },
    {
      title: 'Supporting Metrics',
      headerClassName: 'col-group-header col-group-header-shaded',
      columns: {
        // --- Supporting Metrics (light grey) ---
        programCountry: {
          title: 'Program Country',
          valueFunc: record => record.programCountry || '—',
          sort: record => record.programCountry || '',
          sortType: nullsLastSortType,
          width: 144,
          className: 'col-supporting-metrics col-group-start',
        },
      },
    },
  ]), []);

  const publishedColumns = useMemo(() => ([
    {
      title: '',
      headerClassName: 'col-group-header',
      columns: {
        // --- Basic channel info ---
        creatorName: {
          title: 'Creator Name',
          Component: ({ record }) => <CreatorInList deal={record} onUpdate={handleDealUpdate} />,
          sort: record => record.creator?.title || '',
          sortType: nullsLastSortType,
          width: 250,
          minWidth: 250,
          isFreeze: true,
          left: 0,
          backgroundColor: '#fff',
        },
        category: {
          title: 'Category',
          Component: ({ record }) => {
            const style = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };
            if (record.category) {
              return (
                <Tooltip title={record.category}>
                  <span style={style}>{record.category}</span>
                </Tooltip>
              );
            }
            return (<span>—</span>);
          },
          sort: record => record.category || '',
          sortType: nullsLastSortType,
          width: 120,
        },
      },
    },
    {
      title: 'Deal Info',
      headerClassName: 'col-group-header col-group-header-shaded',
      columns: {
        // --- Deal Info (light grey) ---
        code: {
          title: 'Code',
          valueFunc: record => record.promoCode || '—',
          sort: record => record.promoCode || '',
          sortType: nullsLastSortType,
          width: 130,
          className: 'col-deal-info col-group-start',
        },
        channelType: {
          title: 'Type',
          Component: ({ record }) => (record.dealContentType ? <ChannelTypeLink deal={record} /> : <span>—</span>),
          sort: record => record.dealContentType || '',
          sortType: nullsLastSortType,
          width: 80,
          className: 'col-deal-info',
        },
        rate: {
          title: 'Rate',
          valueFunc: record => formatCurrency(record.totalContractAmount || 0, record.currency?.toLowerCase() || 'usd', 2, true),
          sort: record => record.totalContractAmount || 0,
          sortType: nullsLastSortType,
          width: 120,
          className: 'col-deal-info',
        },
        publishDate: {
          title: 'Publish Date',
          valueFunc: record => record.publishDate ? moment.utc(record.publishDate).format('MM/DD/YY') : '—',
          sort: record => record.publishDate || '',
          sortType: nullsLastSortType,
          width: 135,
          className: 'col-deal-info',
        },
        newOrRebuy: {
          title: 'New/Rebuy',
          Component: ({ record }) => {
            if (!record.newOrExistingCreator) return <span>—</span>;
            return (
              <Badge
                style={{
                  backgroundColor: record.newOrExistingCreator.toLowerCase() === 'new' ? '#70bbfd' : '#e0e0e0',
                  color: record.newOrExistingCreator.toLowerCase() === 'new' ? '#1a5490' : '#333333',
                }}
              >
                {record.newOrExistingCreator.toLowerCase() === 'new' ? 'New' : 'Rebuy'}
              </Badge>
            );
          },
          sort: record => record.newOrExistingCreator || '',
          sortType: nullsLastSortType,
          width: 125,
          className: 'col-deal-info',
        },
        publishedContent: {
          title: 'Published Content',
          Component: ({ record }) => {
            const style = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };
            if (record.mediaContentUrl) {
              return (
                <Tooltip title={record.mediaContentTitle || record.mediaContentUrl}>
                  <a href={record.mediaContentUrl} target="_blank" rel="noopener noreferrer" style={style}>
                    {record.mediaContentTitle || 'View'}
                  </a>
                </Tooltip>
              );
            }
            return (
              <Tooltip title={record.mediaContentTitle || record.mediaContentUrl}>
                <span style={style}>{record.mediaContentTitle || '—'}</span>
              </Tooltip>
            );
          },
          sort: record => record.mediaContentTitle || '',
          sortType: nullsLastSortType,
          width: 200,
          className: 'col-deal-info',
        },
        futureBookings: {
          title: (
            <>
              Future
              <br />
              Bookings
            </>
          ),
          Component: ({ record }) => {
            if (!record.futureBooking || record.futureBooking.length === 0) return <span>—</span>;

            // Filter out current deal from future bookings
            const otherDeals = record.futureBooking.filter(deal => deal.dealId !== record.dealId);

            if (otherDeals.length === 0) return <span>—</span>;

            // Build tooltip content - sort by date ascending
            const sortedDeals = [...otherDeals].sort((a, b) => {
              const dateA = a.contentReleaseDate ? new Date(a.contentReleaseDate) : new Date(0);
              const dateB = b.contentReleaseDate ? new Date(b.contentReleaseDate) : new Date(0);
              return dateA - dateB;
            });
            const totalAmount = otherDeals.reduce((sum, deal) => sum + (deal.amount || 0), 0);
            const tooltipContent = (
              <div style={{ fontSize: '12px' }}>
                {sortedDeals.map(deal => (
                  <div key={deal.dealId} style={{ marginBottom: '4px' }}>
                    <strong>{moment.utc(deal.contentReleaseDate).format('MM/DD/YY')}</strong> - {formatCurrency(deal.amount || 0, record.currency || 'usd', 2, true)}
                  </div>
                ))}
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.3)' }}>
                  <strong>Total Contract Amount: {formatCurrency(totalAmount, record.currency || 'usd', 2, true)}</strong>
                </div>
              </div>
            );

            return (
              <Tooltip title={tooltipContent} arrow>
                <a
                  href="#"
                  className="bold-text"
                >
                  +{otherDeals.length}
                </a>
              </Tooltip>
            );
          },
          sort: record => {
            const otherDeals = record.futureBooking?.filter(deal => deal.dealId !== record.dealId) || [];
            return otherDeals.length;
          },
          sortType: nullsLastSortType,
          width: 120,
          className: 'col-deal-info',
        },
      },
    },
    {
      title: 'Performance',
      headerClassName: 'col-group-header',
      columns: {
        // --- Performance Insights ---
        views: {
          title: 'Views',
          valueFunc: record => formatCompactNumber(record.videoReportData?.videoViews),
          sort: record => record.videoReportData?.videoViews || 0,
          sortType: nullsLastSortType,
          width: 80,
          className: 'col-group-start',
        },
        v30: {
          title: 'V30',
          valueFunc: record => formatCompactNumber(record.videoReportData?.v30),
          sort: record => record.videoReportData?.v30 || 0,
          sortType: nullsLastSortType,
          width: 80,
        },
        videoEr30: {
          title: 'ER30',
          valueFunc: record => formatCompactNumber(record.videoReportData?.videoEr30, 2),
          sort: record => record.videoReportData?.videoEr30 || 0,
          sortType: nullsLastSortType,
          width: 80,
        },
        conversions: {
          title: 'Conv.',
          valueFunc: record => formatCompactNumber(record.conversions || 0),
          sort: record => record.conversions || 0,
          sortType: nullsLastSortType,
          width: 80,
        },
        clicks: {
          title: 'Clicks',
          valueFunc: record => formatCompactNumber(record.clicks || 0),
          sort: record => record.clicks || 0,
          sortType: nullsLastSortType,
          width: 90,
        },
      },
    },
    {
      title: 'CAC',
      headerClassName: 'col-group-header',
      columns: {
        batCac: {
          title: (
            <Tooltip title="Brand All-Time CAC: brand cost / conversions across all time." arrow>
              <span style={{ cursor: 'help' }}>BATCAC</span>
            </Tooltip>
          ),
          valueFunc: record => (record.batCac != null && record.batCac > 0
            ? formatCurrency(record.batCac, record.currency?.toLowerCase() || 'usd', 2, true)
            : '—'),
          sort: record => (record.batCac != null ? record.batCac : null),
          sortType: nullsLastSortType,
          width: 110,
          className: 'col-group-start',
        },
        blvCac: {
          title: (
            <Tooltip title="Brand Last-Video CAC: brand cost / conversions for the latest deal's video (window always extended to the next publish)." arrow>
              <span style={{ cursor: 'help' }}>BLVCAC</span>
            </Tooltip>
          ),
          valueFunc: record => (record.blvCac != null && record.blvCac > 0
            ? formatCurrency(record.blvCac, record.currency?.toLowerCase() || 'usd', 2, true)
            : '—'),
          sort: record => (record.blvCac != null ? record.blvCac : null),
          sortType: nullsLastSortType,
          width: 110,
        },
        ipCac: {
          title: (
            <Tooltip title="In-Period CAC: brand cost / conversions counted within the selected period (respects the active conversion toggles)." arrow>
              <span style={{ cursor: 'help' }}>IPCAC</span>
            </Tooltip>
          ),
          valueFunc: record => (record.ipCac != null && record.ipCac > 0
            ? formatCurrency(record.ipCac, record.currency?.toLowerCase() || 'usd', 2, true)
            : '—'),
          sort: record => (record.ipCac != null ? record.ipCac : null),
          sortType: nullsLastSortType,
          width: 110,
        },
      },
    },
    {
      title: 'Supporting Metrics',
      headerClassName: 'col-group-header col-group-header-shaded',
      columns: {
        // --- Supporting Metrics (light grey) ---
        comments: {
          title: 'Comments',
          valueFunc: record => formatCompactNumber(record.videoReportData?.comments),
          sort: record => record.videoReportData?.comments || 0,
          sortType: nullsLastSortType,
          width: 90,
          className: 'col-supporting-metrics col-group-start',
        },
        engRate: {
          title: '% English',
          valueFunc: record => record.videoReportData?.audienceEng != null ? `${record.videoReportData.audienceEng.toFixed(1)}%` : '—',
          sort: record => record.videoReportData?.audienceEng || 0,
          sortType: nullsLastSortType,
          width: 100,
          className: 'col-supporting-metrics',
        },
        percentFemale: {
          title: '% Female',
          valueFunc: record => record.videoReportData?.audienceFem != null ? `${record.videoReportData.audienceFem.toFixed(0)}%` : '—',
          sort: record => record.videoReportData?.audienceFem || 0,
          sortType: nullsLastSortType,
          width: 95,
          className: 'col-supporting-metrics',
        },
        percentAge25Plus: {
          title: '% Age 25+',
          valueFunc: record => record.videoReportData?.audienceAge25Plus != null ? `${record.videoReportData.audienceAge25Plus.toFixed(0)}%` : '—',
          sort: record => record.videoReportData?.audienceAge25Plus || 0,
          sortType: nullsLastSortType,
          width: 100,
          className: 'col-supporting-metrics',
        },
        programCountry: {
          title: (
            <>
              Program
              <br />
              Country
            </>
          ),
          valueFunc: record => record.programCountry || '—',
          sort: record => record.programCountry || '',
          sortType: nullsLastSortType,
          width: 150,
          className: 'col-supporting-metrics',
        },
      },
    },
  ]), []);

  if (loading) {
    return (
      <Card>
        <CardBody className="text-center py-5">
          <span className="spinner-border" role="status" aria-hidden="true" />
          <p className="mt-3">Loading deals...</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        <style>{`
          .table tbody td, .table th {
            padding: 5px 5px !important;
          }
          table.react-table td.col-deal-info,
          table.react-table th.col-deal-info {
            background-color: #f0f0f075;
          }
          table.react-table td.col-supporting-metrics,
          table.react-table th.col-supporting-metrics {
            background-color: #f0f0f075;
          }
          table.react-table td.col-group-start,
          table.react-table th.col-group-start {
            padding-left: 14px !important;
          }
          table.react-table th.col-group-header {
            font-size: 0.65rem;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: #aaa;
            text-align: center;
            border-bottom: 1px solid #e0e0e0;
            height: 18px !important;
            min-height: 18px !important;
            line-height: 18px;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
          table.react-table th.col-group-header-shaded {
            background-color: #f0f0f075;
          }
          table.react-table th.col-group-header .react-table__column-header {
            justify-content: center;
            padding-right: 0 !important;
            margin-right: 0 !important;
          }
        `}</style>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            {'Confirmed Deals'}
            <Badge color="secondary" className="ml-2">{deals.length}</Badge>
          </h5>
          <UncontrolledButtonDropdown>
            <DropdownToggle color="primary" size="sm" caret>
              Actions
            </DropdownToggle>
            <DropdownMenu right>
              <DropdownItem onClick={handleExportCSV}>
                <i className="fa fa-download mr-2" />Export CSV
              </DropdownItem>
              <DropdownItem onClick={handleCopyCreatorCode}>
                <i className="fa fa-clipboard mr-2" />Copy Creator + Code
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        </div>

        {tableData.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <p>No deals found for the selected filters.</p>
          </div>
        ) : (
          <MatTableToTable
            dataArray={tableData}
            columnGroups={isPipeline ? pipelineColumns : publishedColumns}
            hideToolbar
            filters={{}}
            defaultOrder={{
              orderBy: isPipeline ? 'contentReleaseDate' : 'publishDate',
              order: isPipeline ? 'asc' : 'desc',
            }}
          />
        )}

        {previouslyPublishedRow && (
          <div
            className="d-flex justify-content-between align-items-center mt-2 px-3 py-2"
            style={{
              backgroundColor: '#eaf4fe',
              border: '1px solid #70bbfd',
              color: '#1a5490',
              borderRadius: '4px',
              fontWeight: 600,
            }}
          >
            <span>Conversions from Previously Published Content</span>
            <span>{(previouslyPublishedRow.conversions || 0).toLocaleString()}</span>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

DealsTable.propTypes = {
  deals: PropTypes.arrayOf(PropTypes.shape({
    dealId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    creator: PropTypes.shape({
      name: PropTypes.string,
      hubspotId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      channelId: PropTypes.string,
      mediaChannels: PropTypes.arrayOf(PropTypes.shape({
        channelName: PropTypes.string,
        accountType: PropTypes.string,
      })),
    }),
    category: PropTypes.string,
    brand: PropTypes.string,
    promoCode: PropTypes.string,
    mediaType: PropTypes.string,
    dealContentType: PropTypes.string,
    guaranteedPaymentContractAmount: PropTypes.number,
    totalContractAmount: PropTypes.number,
    contentReleaseDate: PropTypes.string,
    publishDate: PropTypes.string,
    newOrExistingCreator: PropTypes.string,
    futureBooking: PropTypes.array,
    confidence: PropTypes.string,
    mediaContentTitle: PropTypes.string,
    mediaContentUrl: PropTypes.string,
    videoReportData: PropTypes.shape({
      views: PropTypes.number,
      v30: PropTypes.number,
      comments: PropTypes.number,
      audienceEng: PropTypes.number,
      audienceFem: PropTypes.number,
      audienceAge25Plus: PropTypes.number,
    }),
    conversions: PropTypes.number,
    clicks: PropTypes.number,
    batCac: PropTypes.number,
    blvCac: PropTypes.number,
    ipCac: PropTypes.number,
    programCountry: PropTypes.string,
    prospect: PropTypes.object,
  })).isRequired,
  previouslyPublishedRow: PropTypes.shape({
    isPreviouslyPublishedContent: PropTypes.bool,
    conversions: PropTypes.number,
  }),
  loading: PropTypes.bool.isRequired,
  viewType: PropTypes.string.isRequired,
  updateDeal: PropTypes.func.isRequired,
  updateDealsByProspectId: PropTypes.func.isRequired,
};

DealsTable.defaultProps = {
  previouslyPublishedRow: null,
};

const mapStateToProps = state => ({
  deals: selectFilteredDeals(state),
  previouslyPublishedRow: selectPreviouslyPublishedRow(state),
  loading: selectLoading(state),
  viewType: selectViewType(state),
});

const mapDispatchToProps = {
  updateDeal,
  updateDealsByProspectId,
};

export default connect(mapStateToProps, mapDispatchToProps)(DealsTable);
